/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import classnamesBind from 'classnames/bind';
import Icon from '@concur/nui-widgets/lib/Icon/Icon';
import Spinner from '@concur/nui-widgets/lib/Spinner/Spinner';
import { withThemeStyles } from '@concur/ui-theming';
import withNuiFormatter from '../utils/withNuiFormatter';
import compose from '../utils/compose';
import LanguageDropdown from './_LanguageDropdown';
import lowerCase from '../utils/lowerCase';
import Menu from './_Menu';
import MenuItem from './_MenuItem';
import MenuItemToggle from './_MenuItemToggle';
import Navigation from './_Navigation';
import ProductMenu from './_ProductMenu';
import UserProfileMenu from '../UserProfileMenu/UserProfileMenu';
import withErrorBoundary from '../utils/withErrorBoundary';
import { getProfileDisplayText } from '../UserProfileMenu/_ProfileIdentifier';
import * as styles from './Shellbar-*.css';

const CSS_BLOCK = 'sapcnqr-shellbar';

const getActiveMenuItem = (menuData) => {
    const {
        homeMenuItem,
        applicationMenuItems,
        profileMenuItem,
        adminMenuItem,
        helpMenuItem,
        linksMenuItem,
        testDriveMenuItem,
        subscribeMenuItem,
        supportMenuItem,
    } = menuData;

    // create array of all menu items that could be active
    const topLevelMenuItems = [
        homeMenuItem,
        testDriveMenuItem,
        ...(applicationMenuItems || []),
        adminMenuItem,
        linksMenuItem,
        helpMenuItem,
        profileMenuItem,
        subscribeMenuItem,
        supportMenuItem,
    ];

    const matchingMenuItems = topLevelMenuItems.filter((item) => item && item.isActive);

    return (matchingMenuItems.length > 0 ? matchingMenuItems[0] : homeMenuItem);
};

const Shellbar = (props) => {
    const {
        actingAs,
        avatarSrc,
        className,
        classNameMap,
        formatter,
        fullName,
        isGov,
        isLoggedIn,
        isMayFourth,
        logoutUser,
        menuData,
        onEndSession,
        onStartSession,
        showLanguageDropdown,
    } = props;

    const {
        homeMenuItem,
        profileMenuItem,
        helpMenuItem,
        pageMenuItems,
    } = menuData;

    const classnames = classnamesBind.bind(classNameMap);

    const secondaryNavRef = React.createRef();
    const [appHeaderWidth, setAppHeaderWidth] = useState(0);
    const [showSpinner, setShowSpinner] = useState(false);
    const [profileMenuExpanded, setProfileMenuExpanded] = useState(false);

    const classes = classnames(CSS_BLOCK, className);

    const primaryNavClasses = classnames(
        `${CSS_BLOCK}__nav`,
        `${CSS_BLOCK}__nav--primary`,
    );

    const secondaryNavClasses = classnames(
        `${CSS_BLOCK}__nav`,
        `${CSS_BLOCK}__nav--secondary`,
    );

    const homeIconClasses = classnames(
        `${CSS_BLOCK}__home-icon`,
        {
            [`${CSS_BLOCK}__home-icon--gov`]: isGov,
        },
    );

    const profileButtonLabel = profileMenuExpanded
        ? formatter.formattedMessage({ id: 'CoreUI.profile.closeProfileMenu' })
        : formatter.formattedMessage({ id: 'CoreUI.profile.openProfileMenu' });

    const { allUsers, companyName, otherUserName } = actingAs;
    const isActingForOthers = !!(allUsers || companyName || otherUserName);

    const generateProfileButton = () => {
        const profileDisplayText = getProfileDisplayText({
            actingAs,
            formatter,
            fullName,
            shortNames: true,
        });

        if (isActingForOthers) {
            return (
                <>
                    <Icon
                        ariaHidden
                        iconName="user-admin"
                        size="md"
                    />
                    <div className={classnames(`${CSS_BLOCK}__profile-description`)} title={profileDisplayText}>
                        {profileDisplayText}
                    </div>
                    <span className={classnames(`${CSS_BLOCK}__screen-reader-only`)}>
                        {profileButtonLabel}
                    </span>
                </>
            );
        }

        return avatarSrc
            ? (
                <>
                    <img
                        alt=""
                        className={classnames(`${CSS_BLOCK}__avatar`)}
                        src={avatarSrc}
                    />
                    <span className={classnames(`${CSS_BLOCK}__screen-reader-only`)}>
                        {profileButtonLabel}
                    </span>
                </>
            )
            : (
                <>
                    <Icon
                        ariaHidden
                        className={classnames(`${CSS_BLOCK}__icon`)}
                        iconName="user"
                        size="md-minus"
                    />
                    <span className={classnames(`${CSS_BLOCK}__screen-reader-only`)}>
                        {profileButtonLabel}
                    </span>
                </>
            );
    };

    const activeMenuItem = getActiveMenuItem(menuData) || {};

    const getWidth = (ref) => ref?.current?.offsetWidth || 0;

    const handleClick = (expanded) => setProfileMenuExpanded(expanded);

    const handleClose = () => setProfileMenuExpanded(false);

    const handleStartLoading = () => {
        setShowSpinner(true);
    };

    const handleEndLoading = () => {
        setShowSpinner(false);
    };

    const handleResize = () => {
        setAppHeaderWidth(getWidth(secondaryNavRef));
    };
    // runs only on load - this gets us our default utility and container widths
    useEffect(handleResize, []);

    // runs on every resize
    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });

    return (
        <div className={classes}>
            {isLoggedIn ? (
                <Navigation
                    ariaLabel={formatter.formattedMessage({ id: 'CoreUI.primaryMenuAriaLabel' })}
                    className={primaryNavClasses}
                >
                    <Menu
                        className={classnames(`${CSS_BLOCK}__menu`)}
                    >
                        {homeMenuItem && (
                            <MenuItem
                                {...homeMenuItem}
                                classNameMap={classNameMap}
                                cssBlock={CSS_BLOCK}
                                className={classnames(`${CSS_BLOCK}__listitem--home`)}
                                isActive={false} // home menu item is never shown as active
                                key="dashboard"
                            >
                                <i aria-hidden="true" className={homeIconClasses} id="app-header-shellbar-logo" />
                            </MenuItem>
                        )}
                        <MenuItemToggle
                            buttonAriaLabel={activeMenuItem.name}
                            classNameMap={classNameMap}
                            cssBlock={CSS_BLOCK}
                            buttonClassName={classnames(`${CSS_BLOCK}__button--product`)}
                            buttonProps={{
                                'data-test': 'sapcnqr-shellbar-product-menu',
                            }}
                            overlayContentClassName={classnames(`${CSS_BLOCK}__product-menu`)}
                            content={activeMenuItem.name}
                        >
                            <ProductMenu isFioriProductMenu menuData={menuData} />
                        </MenuItemToggle>
                    </Menu>
                    {(helpMenuItem || profileMenuItem) && (
                        <Menu
                            className={classnames(`${CSS_BLOCK}__menu`, `${CSS_BLOCK}__menu--utility`)}
                        >
                            {helpMenuItem && (
                                <MenuItemToggle
                                    buttonAriaLabel={helpMenuItem.name}
                                    buttonProps={{ 'data-test': helpMenuItem.id ? `menu__listitem-${lowerCase(helpMenuItem.id)}` : null }}
                                    classNameMap={classNameMap}
                                    cssBlock={CSS_BLOCK}
                                    content={(
                                        <Icon
                                            ariaHidden
                                            className={classnames(`${CSS_BLOCK}__icon`)}
                                            iconName="messaging-help"
                                            size="md-minus"
                                        />
                                    )}
                                    hideCaret
                                    popperPlacement="bottom-end"
                                >
                                    <Menu
                                        className={classnames(`${CSS_BLOCK}__submenu`, `${CSS_BLOCK}__submenu--toggle`)}
                                        id={helpMenuItem.id}
                                        isSubItem
                                    >
                                        {helpMenuItem.subItems.map((item, index) => (
                                            <MenuItem
                                                {...item}
                                                classNameMap={classNameMap}
                                                cssBlock={CSS_BLOCK}
                                                key={`help${index}`}
                                            />
                                        ))}
                                    </Menu>
                                </MenuItemToggle>
                            )}
                            {profileMenuItem && (
                                <>
                                    <MenuItemToggle
                                        buttonClassName={isActingForOthers ? classnames(`${CSS_BLOCK}__acting-as-button`) : ''}
                                        buttonProps={{ 'data-test': profileMenuItem.id ? `menu-${lowerCase(profileMenuItem.id)}` : null }}
                                        classNameMap={classNameMap}
                                        cssBlock={CSS_BLOCK}
                                        content={generateProfileButton()}
                                        onClick={handleClick}
                                        onClose={handleClose}
                                        hideCaret
                                        popperPlacement="bottom-end"
                                        includeTargetInTabFlow
                                    >
                                        <UserProfileMenu
                                            actingAs={actingAs}
                                            fullName={fullName}
                                            isGov={isGov}
                                            logoutUser={logoutUser}
                                            onEndLoading={handleEndLoading}
                                            onEndSession={onEndSession}
                                            onStartLoading={handleStartLoading}
                                            onStartSession={onStartSession}
                                            profileUrl={profileMenuItem.url}
                                        />
                                    </MenuItemToggle>
                                    <Spinner
                                        message={formatter.formattedMessage({ id: 'CoreUI.switchingActingAsView' })}
                                        type="fullScreen"
                                        visible={showSpinner}
                                    />
                                </>
                            )}
                        </Menu>
                    )}
                </Navigation>
            ) : (
                <span className={classnames(`${CSS_BLOCK}__nav`)}>
                    <span className={classnames(`${CSS_BLOCK}__brand`)}>
                        <i
                            aria-label="SAP Concur"
                            className={homeIconClasses}
                            role="img"
                        />
                    </span>
                    {showLanguageDropdown && (
                        <LanguageDropdown
                            classNameMap={classNameMap}
                            cssBlock={CSS_BLOCK}
                            isMayFourth={isMayFourth}
                        />
                    )}
                </span>
            )}
            {isLoggedIn && pageMenuItems && pageMenuItems.length > 0 && (
                <Navigation
                    ariaLabel={formatter.formattedMessage({ id: 'CoreUI.secondaryMenuAriaLabel' })}
                    className={secondaryNavClasses}
                    ref={secondaryNavRef}
                >
                    <Menu
                        className={classnames(`${CSS_BLOCK}__menu`)}
                        collapsible
                        appHeaderWidth={appHeaderWidth}
                    >
                        {pageMenuItems.map((item, index) => (
                            (item.subItems) ? (
                                <MenuItemToggle
                                    buttonAriaLabel={item.name}
                                    buttonClassName={classnames(`${CSS_BLOCK}__button--secondary`)}
                                    classNameMap={classNameMap}
                                    content={item.name}
                                    cssBlock={CSS_BLOCK}
                                    key={`pageSubMenu${index}`}
                                    popperPlacement="bottom-start"
                                >
                                    <Menu
                                        className={classnames(`${CSS_BLOCK}__submenu`, `${CSS_BLOCK}__submenu--toggle`)}
                                        isSubItem
                                    >
                                        {item.subItems.map((subItem, subIndex) => (
                                            <MenuItem
                                                {...subItem}
                                                classNameMap={classNameMap}
                                                cssBlock={CSS_BLOCK}
                                                key={`secondary${subIndex}`}
                                                hasPopover
                                            />
                                        ))}
                                    </Menu>
                                </MenuItemToggle>
                            ) : (
                                <MenuItem
                                    {...item}
                                    classNameMap={classNameMap}
                                    cssBlock={CSS_BLOCK}
                                    key={`secondary${index}`}
                                />
                            )
                        ))}
                        <MenuItem
                            classNameMap={classNameMap}
                            cssBlock={CSS_BLOCK}
                            isOverflow
                            key="overflowSecondary"
                        />
                    </Menu>
                </Navigation>
            )}
        </div>
    );
};

Shellbar.displayName = 'Shellbar';

Shellbar.cssBlock = CSS_BLOCK;

Shellbar.defaultProps = {
    actingAs: {},
};

export default withThemeStyles(compose(
    withNuiFormatter,
    withErrorBoundary,
)(Shellbar), styles);
